const getException = (event) => {
    const values = event?.exception?.values;
    return Array.isArray(values) ? values[values.length - 1] : undefined;
};

const getUserAgent = (event) => {
    const headers = event?.request?.headers;
    if (Array.isArray(headers)) {
        const entry = headers.find((header) => (
            Array.isArray(header) &&
            typeof header[0] === 'string' &&
            header[0].toLowerCase() === 'user-agent'
        ));
        return typeof entry?.[1] === 'string' ? entry[1] : '';
    }
    const userAgent = headers?.['User-Agent'] || headers?.['user-agent'];
    return typeof userAgent === 'string' ? userAgent : '';
};

const hasFrameFrom = (exception, prefix) => {
    const frames = exception?.stacktrace?.frames;
    return Array.isArray(frames) && frames.some((frame) => (
        typeof frame?.filename === 'string' && frame.filename.startsWith(prefix)
    ));
};

const hasFunctionFrame = (exception, functionName) => {
    const frames = exception?.stacktrace?.frames;
    return Array.isArray(frames) && frames.some((frame) => frame?.function === functionName);
};

const hasFunctionFrameFrom = (exception, prefix, functionName) => {
    const frames = exception?.stacktrace?.frames;
    return Array.isArray(frames) && frames.some((frame) => (
        typeof frame?.filename === 'string' &&
        frame.filename.startsWith(prefix) &&
        frame.function === functionName
    ));
};

const hasFailedFetchFrom = (event, prefix) => {
    const breadcrumbs = event?.breadcrumbs;
    return Array.isArray(breadcrumbs) && breadcrumbs.some((breadcrumb) => (
        breadcrumb?.type === 'http' &&
        breadcrumb?.category === 'fetch' &&
        breadcrumb?.level === 'error' &&
        breadcrumb?.data?.method === 'GET' &&
        typeof breadcrumb?.data?.url === 'string' &&
        breadcrumb.data.url.startsWith(prefix)
    ));
};

const WEBVIEW_BRIDGE_ERRORS = new Set([
    'Error invoking log: Java object is gone',
    'Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone',
    'Error invoking enableButtonsClickedMetaDataLogging: Java object is gone',
    'Error invoking postMessage: Java object is gone',
]);

const CLOUDFLARE_BEACON_COMPATIBILITY_ERRORS = new Set([
    'this.i.at is not a function',
    't.entries.at is not a function',
    'Unexpected token .',
    "Cannot assign to read only property 'pushState' of object '#<History>'",
]);

const CEF_SHARP_CRAWLER_REJECTION =
    'Non-Error promise rejection captured with value: Object Not Found Matching Id:1, MethodName:update, ParamCount:4';

const SENSITIVE_KEYS = new Set([
    'authorization',
    'current_password',
    'email',
    'new_password',
    'password',
    'password_confirm',
    'postal_code',
    'token',
]);

export const redactSensitiveData = (value) => {
    if (typeof value === 'string') {
        try {
            return redactSensitiveData(JSON.parse(value));
        } catch (_error) {
            return value;
        }
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            value[index] = redactSensitiveData(item);
        });
        return value;
    }
    if (value && typeof value === 'object') {
        Object.entries(value).forEach(([key, item]) => {
            value[key] = SENSITIVE_KEYS.has(key.toLowerCase())
                ? '[redacted]'
                : redactSensitiveData(item);
        });
        return value;
    }
    return value;
};

export const shouldInitializeErrorReporting = (hostname) => (
    hostname !== 'localhost' && !hostname.endsWith('.local')
);

export const beforeSend = (event) => {
    const exception = getException(event);
    const value = typeof exception?.value === 'string' ? exception.value : '';
    const userAgent = getUserAgent(event);
    const isThirdPartyIosWebView = /Instagram|FBAN|FBAV/i.test(userAgent);
    const isAndroidWebView = /; wv\)|\bAndroid WebView\b/i.test(userAgent);

    if (value.includes("window.webkit.messageHandlers") && isThirdPartyIosWebView) {
        return null;
    }

    if (WEBVIEW_BRIDGE_ERRORS.has(value) && isAndroidWebView) {
        return null;
    }

    if (exception?.type === 'TypeError' &&
        value === "Cannot read properties of undefined (reading 'value')" &&
        isAndroidWebView &&
        /FB_IAB|FBAV/i.test(userAgent) &&
        hasFunctionFrame(exception, 'Object.setContactAutofillValuesFromBridge')) {
        return null;
    }

    if (value === 'TrackerStorageType is not defined' &&
        hasFrameFrom(exception, 'https://consent.cookiebot.com/')) {
        return null;
    }

    if (value === 'Invalid call to runtime.sendMessage(). Tab not found.' &&
        hasFrameFrom(exception, 'chrome-extension://')) {
        return null;
    }

    if (value === 'Permission denied to access object' &&
        hasFrameFrom(exception, 'https://consent.cookiebot.com/')) {
        return null;
    }

    if (value === 'Verification timed out' &&
        hasFrameFrom(exception, 'chrome-extension://')) {
        return null;
    }

    if (value === 'Illegal invocation' &&
        hasFrameFrom(exception, 'https://consent.cookiebot.com/') &&
        hasFunctionFrameFrom(
            exception,
            'chrome-extension://dbilanlcioamaadkbepcenpombaejbla/',
            'document.createEvent',
        )) {
        return null;
    }

    if (value === "Cannot set properties of null (setting 'onclick')" &&
        hasFunctionFrameFrom(
            exception,
            'https://consent.cookiebot.com/',
            'window.CookieControl.Cookie.applyOverrideEventListeners',
        )) {
        return null;
    }

    if (value === "Cannot read properties of undefined (reading 'then')" &&
        hasFunctionFrameFrom(
            exception,
            'https://consent.cookiebot.com/',
            'window.CookieControl.Cookie.applyOverrideEventListeners',
        )) {
        return null;
    }

    if (value === "Cannot read properties of null (reading 'classList')" &&
        hasFunctionFrameFrom(
            exception,
            'https://consent.cookiebot.com/',
            'startSpinner',
        )) {
        return null;
    }

    if (value === 'invalid origin' && userAgent.includes('DuckDuckGo/')) {
        return null;
    }

    if (exception?.type === 'UnhandledRejection' && value === CEF_SHARP_CRAWLER_REJECTION) {
        return null;
    }

    if (CLOUDFLARE_BEACON_COMPATIBILITY_ERRORS.has(value) &&
        hasFrameFrom(exception, 'https://static.cloudflareinsights.com/beacon.min.js')) {
        return null;
    }

    if (exception?.type === 'TypeError' &&
        value === 'Load failed (oogst-assets.s3.eu-west-1.amazonaws.com)' &&
        hasFailedFetchFrom(event, 'https://oogst-assets.s3.eu-west-1.amazonaws.com/uploads/')) {
        return null;
    }

    return redactSensitiveData(event);
};

export const safeStorage = {
    get(key, fallback = null, resolveStorage = () => window.localStorage) {
        try {
            const storage = resolveStorage();
            return storage.getItem(key) ?? fallback;
        } catch (_error) {
            return fallback;
        }
    },
    set(key, value, resolveStorage = () => window.localStorage) {
        try {
            const storage = resolveStorage();
            storage.setItem(key, value);
            return true;
        } catch (_error) {
            // Storage can be blocked in private browsing and embedded webviews.
            return false;
        }
    },
    remove(key, resolveStorage = () => window.localStorage) {
        try {
            const storage = resolveStorage();
            storage.removeItem(key);
            return true;
        } catch (_error) {
            return false;
        }
    },
};

export const persistPreferredLanguage = (
    language,
    reload = () => window.location.reload(),
    resolveStorage,
) => {
    const stored = safeStorage.set('oogst_preferred_language', language, resolveStorage);
    if (stored) {
        reload();
    }
    return stored;
};

export const shouldPersistPreferredLanguage = (preferredLanguage, companyLanguage) => (
    preferredLanguage !== companyLanguage
);
